/**
 * Профиль: аккаунт, подписка, письма, документы, выход.
 *
 * Стоит в шапке главной, потому что это единственные вопросы, которые человек
 * задаёт про себя, а не про деньги: «под кем я вошёл», «за что плачу», «что вы
 * делаете с моими данными» и «как выйти». Раньше всё это лежало во вкладке
 * «Настройка» вперемешку с доходами и платежами — то есть там, где человек
 * ищет цифры, а не себя.
 *
 * Инициалы на кнопке — только если человек назвал имя. Выдумывать их из почты
 * нельзя: «al» из `aleksey.petrov@…` на общем устройстве окажется чужим.
 */
import { useState } from 'react';
import { SheetWrap } from './sheet.js';
import { Tip } from './tip.js';
import { setProfileName, type AppState } from './store.js';
import type { User } from './sync.js';
import { Icon } from './icon.js';
import { ForgotPassword } from './password-reset.js';
import { flash } from './flash.js';
import { SetRow } from './set-row.js';

/** Инициалы из имени: одно слово — одна буква, два — две. */
export function initialsOf(name: string | undefined): string | null {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return null;
  const letters = words.slice(0, 2).map((w) => [...w][0]?.toUpperCase() ?? '');
  return letters.join('') || null;
}

/**
 * Нейтральный значок: имени нет, и придумывать его не за что. Сплошной, а не
 * контурный: на цветном кружке тонкий силуэт терялся (выбор владельца 15.10.2026).
 */
const PersonIcon = () => <Icon name="user" size={14} solid />;

/**
 * Профиль и меню в шапке главной — одна стеклянная капсула (макет 15.10.2026).
 *
 * Раньше рядом стояли серый кружок 32 px и белый квадрат 38 px с набранным
 * «⋯»: разные форма и размер, шапка выглядела собранной из разных наборов.
 * Капсула — из того же стекла, что нижнее меню; внутри — кружок в цветах
 * логотипа.
 */
export const ProfileCapsule = ({ name, onProfile, onMenu, bell = null, onBell }: {
  name: string | undefined; onProfile: () => void; onMenu: () => void;
  /**
   * «Центр уведомлений» — колокольчик внутри капсулы (выбор владельца
   * 22.10.2026): шапка не обрастает кружками. Цифра — сколько нового;
   * `null` — без аккаунта центра нет.
   */
  bell?: number | null; onBell?: () => void;
}) => {
  const initials = initialsOf(name);
  return (
    <div className="hcaps">
      <button className="hava" onClick={onProfile}
        aria-label={initials ? `Профиль: ${name}` : 'Профиль'}>
        {initials ?? <PersonIcon />}
      </button>
      <i className="hsep" aria-hidden="true" />
      {bell !== null && (
        <button className="hbell" onClick={onBell}
          aria-label={bell > 0 ? `Центр уведомлений, новых: ${bell}` : 'Центр уведомлений'}>
          <Icon name="bell" size={17} />
          {bell > 0 && <i className="hbell-n">{bell > 9 ? '9+' : bell}</i>}
        </button>
      )}
      {/* Шестерёнка вместо «•••» (27.10.2026): значок сам говорит, что внутри, — «Настройки», бывшее «Ещё». */}
      <button className="hdots" onClick={onMenu} aria-label="Настройки">
        <Icon name="gear" size={17} />
      </button>
    </div>
  );
};

export function ProfileSheet({
  state, user, syncing = false, syncNote = null, onSync, onClose, onAuth, onLogout, onSaved, onDocs, onDelete, children, mail,
}: {
  state: AppState;
  user: User | null;
  /** Идёт синхронизация — пометка у «Аккаунта». */
  syncing?: boolean;
  /** Что с синхронизацией, если не всё гладко. */
  syncNote?: string | null;
  /** «Загрузить с сервера» — переехало сюда из «Ещё» вместе с аккаунтом. */
  onSync?: () => void;
  onClose: () => void;
  onAuth: () => void;
  onLogout: () => void;
  onSaved: () => void;
  onDocs: () => void;
  /** Удаление аккаунта: необратимое действие, поэтому отдельным экраном. */
  onDelete: () => void;
  /** Блок подписки: он ходит на сервер и живёт отдельной жизнью. */
  children?: React.ReactNode;
  /** Письма — после аккаунта: подписка сверху, как карточка в кошельке. */
  mail?: React.ReactNode;
}) {
  const [name, setName] = useState(state.profileName ?? '');
  const [busy, setBusy] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  if (changingPassword && user) return <ForgotPassword context="profile" email={user.email}
    onBack={() => setChangingPassword(false)} onClose={() => setChangingPassword(false)} />;

  const initials = initialsOf(name);
  return (
    <SheetWrap title="Профиль" onClose={onClose}>
      {/*
        Шапка профиля — кружок с инициалами, как в шапке главной, и имя рядом
        (03.11.2026). Прежде сверху стояло голое поле «Как тебя зовут», а
        кнопки аккаунта шли серыми плашками во всю ширину.
      */}
      <section className="blk profhead">
        <span className="setava lg">{initials ?? <PersonIcon />}</span>
        <div className="profname">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Как тебя зовут"
            aria-label="Как тебя зовут" />
          <span>{user ? user.email : 'без аккаунта'}<Tip>Первые буквы имени появятся на кнопке профиля на главной.</Tip></span>
        </div>
      </section>
      {name.trim() !== (state.profileName ?? '') && (
        <button className="btn pri" disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await setProfileName(name);
              flash('Имя сохранено');
              onSaved();
            } finally { setBusy(false); }
          }}>Сохранить имя</button>
      )}

      {children}

      <section className="blk">
        <div className="blkhead"><b>Аккаунт</b>
          {syncing && <span className="tag soft">синк…</span>}</div>
        {user ? (
          <>
            {/* Нужна: с сервера приложение само берёт данные только на пустом устройстве,
                и правки с компьютера телефон иначе не увидит (03.11.2026). */}
            {onSync && <SetRow icon="cloud-arrow-down" title="Загрузить с сервера"
              sub={syncNote ?? 'данные менялись на другом устройстве — забрать сюда'} onClick={onSync} />}
            <SetRow icon="key" title="Сменить пароль" sub="письмо со ссылкой придёт на почту" onClick={() => setChangingPassword(true)} />
            <SetRow icon="arrow-right-from-bracket" title="Выйти" sub="данные останутся в аккаунте" onClick={onLogout} />
            {/* Удаление стоит рядом с выходом: это тоже способ уйти, только
                окончательный. Прятать его — значит заставлять человека
                писать в поддержку за тем, что он вправе сделать сам. */}
            <SetRow icon="trash-can" danger title="Удалить аккаунт" sub="вместе с данными на сервере" onClick={onDelete} />
          </>
        ) : (
          <>
            <div className="li"><div className="nm"><b>Без аккаунта
              <Tip>Расчёты и после входа идут на устройстве. Аккаунт нужен только чтобы
                не потерять данные и открывать их с телефона и компьютера.</Tip></b>
              <span>
                Данные живут только в этом браузере: чистка кэша или другое
                устройство — и картины нет.{syncNote ? ` ${syncNote}` : ''}
              </span></div></div>
            <button className="btn pri" onClick={onAuth}>Создать аккаунт или войти</button>
          </>
        )}
      </section>

      {mail}

      <section className="blk">
        <SetRow icon="file-contract" title="Условия, данные и согласия"
          sub="что приложение делает с данными и на что дано согласие" onClick={onDocs} />
      </section>
    </SheetWrap>
  );
}
